import { apiClient } from '@/lib/api-client';
import type { User } from '@/types';

export type UserProfile = User;

export interface CreateProfileRequest {
  name: string;
  email: string;
  companyName?: string;
  timezone?: string;
  hourlyRate?: number;
}

export interface UpdateUserProfileRequest {
  id: string; // Always 'PROFILE' for user profiles
  name?: string;
  hourlyRate?: number;
  timezone?: string;
  companyName?: string;
  companyAddress?: string;
  defaultCurrency?: string;
  defaultPaymentTerms?: string;
  defaultTermsAndConditions?: string;
  defaultTaxRate?: number;
  tradeLicenseNumber?: string;
  vatNumber?: string;
  taxId?: string;
  phone?: string;
  website?: string;
  bankName?: string;
  bankAccountName?: string;
  iban?: string;
  swiftBic?: string;
  paymentMethods?: string[];
  paymentInstructions?: string;
}

export class ProfilesService {
  /**
   * Create a new user profile (first-time setup)
   */
  async createProfile(data: CreateProfileRequest): Promise<User> {
    return await apiClient.post<User>('/profiles', data);
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<User> {
    return await apiClient.get<User>('/profiles');
  }

  /**
   * Check if current user has a profile
   */
  async profileExists(): Promise<boolean> {
    try {
      await this.getProfile();
      return true;
    } catch (err: any) {
      if (err.statusCode === 404) {
        return false;
      }
      throw err;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateUserProfileRequest): Promise<User> {
    return await apiClient.patch<User>('/profiles', data);
  }

  /**
   * Create or update user profile (convenience method)
   */
  async createOrUpdateProfile(data: CreateProfileRequest | UpdateUserProfileRequest): Promise<User> {
    const exists = await this.profileExists();
    if (exists) {
      return await this.updateProfile(data as UpdateUserProfileRequest);
    } else {
      return await this.createProfile(data as CreateProfileRequest);
    }
  }

  /**
   * Delete user account (soft delete)
   */
  async deleteAccount(): Promise<void> {
    return await apiClient.delete<void>('/profiles/account');
  }
}

export const profilesService = new ProfilesService();
